import { brokerName } from '@/utils/brokers'
import { useI18n } from 'vue-i18n'

/**
 * Checks if a given date is a weekday and falls within the specified time ranges.
 * @param {Date} date - The date to check (usually the current date by default).
 * @param {[Date, Date]} rangeOne - The first time range specified as an array of two dates (in HHMM format).
 * @param {[Date, Date]} rangeTwo - The second time range specified as an array of two dates (in HHMM format).
 * @returns {boolean} - Returns true if the date falls within any of the specified time ranges on a weekday, false otherwise.
 * @example
 * const date = new Date(2023, 6, 4, 10, 0) // Thursday, July 4th, 2023, 10:00 AM
 * const range: [Date, Date] = [new Date(0, 0, 0, 9, 30), new Date(0, 0, 0, 12, 45)]
 * const range2: [Date, Date] = [new Date(0, 0, 0, 14, 0), new Date(0, 0, 0, 18, 0)]
 * const result = isWeekdayInRange(date, range, range2)
 *
 * expect(result).toBe(true)
 */
export function isWeekdayInRange(date: Date, rangeOne: [Date, Date], rangeTwo?: [Date, Date]): boolean {
  if (!isOnWeekday(date)) return false

  const time = date.getHours() * 100 + date.getMinutes()
  const isInRange1 = checkTimeInHourRange(time, rangeOne)
  const isInRange2 = rangeTwo ? checkTimeInHourRange(time, rangeTwo) : false

  return isInRange1 || isInRange2
}

/**
 * Checks if a given date is a weekday.
 * @param {Date} date
 * @returns {boolean} - Returns true if the date is a weekday, false otherwise.
 */
export function isOnWeekday(date: Date): boolean {
  const day = date.getDay()
  return day !== 0 && day !== 6
}

/**
 * Checks if a given time falls within the specified time range.
 * @param {number} time - The time to check in the format HHMM (e.g., 0930 for 09:30).
 * @param {[Date, Date]} range - The time range specified as an array of two dates.
 * @returns {boolean} - Returns true if the time falls within the specified range, false otherwise.
 * @example
 * const range: [Date, Date] = [new Date(0, 0, 0, 9, 30), new Date(0, 0, 0, 12, 45)]
 * const range2: [Date, Date] = [new Date(0, 0, 0, 14, 0), new Date(0, 0, 0, 18, 0)]
 * const result = checkTimeInHourRange(range, range2)
 *
 * expect(result).toBe(true)
 */
export function checkTimeInHourRange(time: number, range: [Date, Date]): boolean {
  const [startTime, endTime] = range
  const start = startTime.getHours() * 100 + startTime.getMinutes()
  const end = endTime.getHours() * 100 + endTime.getMinutes()

  return time >= start && time <= end
}

/**
 * Returns a relative date from now in days.
 * @param {string} date - The date to compare to.
 * @param {string} secondDate - The second date to compare to (defaults to the current date).
 * @returns {string} -  The relative date in days.
 * @example
 * const result = relativeDateInDays('2021-07-04T10:00:00.000Z')
 * expect(result).toBe(2)
 */
export function relativeDateInDays(date: string, secondDate?: string): number {
  const givenDate = new Date(date)
  const secondGivenDate = secondDate ? new Date(secondDate) : new Date()

  const diffInMillis = secondGivenDate.getTime() - givenDate.getTime()
  return Math.floor(diffInMillis / (1000 * 60 * 60 * 24))
}

export const formatDuration = (date: Date) => {
  const { t } = useI18n()
  const fromDate = date
  const currentDate = new Date()

  if (isNaN(fromDate.getTime())) {
    return 'Date invalide'
  }

  const diffInMilliseconds = currentDate.getTime() - fromDate.getTime()
  const totalDays = Math.floor(diffInMilliseconds / (24 * 60 * 60 * 1000))
  if (totalDays < 1) {
    return ''
  } else if (totalDays < 30) {
    return t(`pages.account.yago_member_days`, { days: totalDays, brokerName: brokerName() })
  } else if (totalDays < 365) {
    const months = Math.floor(totalDays / 30)
    const remainingDays = totalDays % 30
    return `${t(`pages.account.yago_member_months`, { months: months, brokerName: brokerName() })} ${
      remainingDays > 0 ? t(`pages.account.remaining_days`, { days: remainingDays }) : ''
    }`
  } else {
    const years = Math.floor(totalDays / 365)
    const remainingMonths = Math.floor((totalDays % 365) / 30)
    return `${t(`pages.account.yago_member_years`, { years: years, brokerName: brokerName() })} ${
      remainingMonths > 0 ? t(`pages.account.remaining_months`, { months: remainingMonths }) : ''
    }`
  }
}

export const calculateRemainingTime = (date: string) => {
  const MS_PER_MINUTE = 1000 * 60
  const MS_PER_HOUR = MS_PER_MINUTE * 60

  const nextSubmissionDate = new Date(date)
  const currentTime = new Date()
  const timeDifferenceMs = nextSubmissionDate.getTime() - currentTime.getTime()

  return {
    hours: Math.floor(timeDifferenceMs / MS_PER_HOUR),
    minutes: Math.floor((timeDifferenceMs % MS_PER_HOUR) / MS_PER_MINUTE)
  }
}
